
/**
 * fileUpload.ts
 * 
 * Utility functions for handling file uploads to Supabase storage.
 * Provides methods for uploading files, generating URLs, and managing file metadata.
 */

import { supabase } from '@/integrations/supabase/client';
import { FileAttachment } from '@/types/posit';
import { v4 as uuidv4 } from 'uuid';
import { MAX_FILE_SIZE } from '@/utils/fileValidation';

// Object URL tracking for memory leak prevention
const objectURLs = new Map<string, string>();

/**
 * Creates an object URL and tracks it for later cleanup
 */
export const createAndTrackObjectURL = (file: File): string => {
  const url = URL.createObjectURL(file);
  objectURLs.set(url, file.name);
  return url;
};

/**
 * Revokes an object URL and removes it from tracking
 */
export const revokeObjectURL = (url: string | undefined): void => {
  if (!url) return;
  
  // Only revoke URLs that start with blob: or file:
  if (url.startsWith('blob:') || url.startsWith('file:')) {
    try {
      URL.revokeObjectURL(url);
      objectURLs.delete(url);
    } catch (error) {
    }
  }
};

/**
 * Revokes all tracked object URLs
 */
export const revokeAllObjectURLs = (): void => {
  objectURLs.forEach((name, url) => {
    try {
      URL.revokeObjectURL(url);
    } catch (error) {
    }
  });
  objectURLs.clear();
};

/**
 * Converts a File object to a FileAttachment object with a temporary URL
 */
export const fileToAttachment = (file: File): FileAttachment => {
  return {
    id: uuidv4(),
    name: file.name,
    mimeType: file.type,
    source: 'upload',
    url: createAndTrackObjectURL(file),
    size: file.size
  };
};

/**
 * Uploads a file to Supabase storage and returns a permanent URL
 */
export const uploadFileToStorage = async (
  file: File, 
  userId: string,
  positId: string
): Promise<{ path: string; url: string }> => {
  // Create a unique file path
  const fileExt = file.name.split('.').pop();
  const fileName = `${uuidv4()}.${fileExt}`;
  const filePath = `${userId}/${positId}/${fileName}`;
  
  // Upload the file to Supabase storage
  const { data, error } = await supabase.storage
    .from('posit-attachments')
    .upload(filePath, file, {
      cacheControl: '3600',
      upsert: false
    });
  
  if (error) {
    throw new Error(`Failed to upload file: ${error.message}`);
  }
  
  // Return the storage path. We will generate signed URLs when needed.
  return {
    path: data.path,
    url: filePath
  };
};

/**
 * Uploads multiple files to Supabase storage and updates their URLs
 */
export const uploadFilesToStorage = async (
  files: { file: File; attachment: FileAttachment }[],
  userId: string,
  positId: string
): Promise<FileAttachment[]> => {
  const updatedAttachments: FileAttachment[] = [];
  
  for (const { file, attachment } of files) {
    try {
      const { url } = await uploadFileToStorage(file, userId, positId);
      
      // Update the attachment with the permanent URL
      updatedAttachments.push({
        ...attachment,
        url
      });
    } catch (error) {
      // Keep the attachment with the temporary URL
      updatedAttachments.push(attachment);
    }
  }
  
  return updatedAttachments;
};

/**
 * Stores file references in the database
 */
export const storeFileReferences = async (
  attachments: FileAttachment[],
  userId: string,
  positId: string
): Promise<void> => {
  if (attachments.length === 0) return;
  
  const fileReferences = attachments.map(file => ({
    posit_id: positId,
    user_id: userId,
    file_id: file.id,
    file_name: file.name,
    file_type: file.mimeType,
    source: file.source,
    file_url: file.url,
    created_at: new Date().toISOString()
  }));
  
  const { error } = await supabase
    .from('posit_attachments')
    .insert(fileReferences);
  
  if (error) {
    throw new Error(`Failed to store file references: ${error.message}`);
  }
};

/**
 * Fetches file attachments for a specific posit
 */
export const fetchPositAttachments = async (
  positId: string
): Promise<FileAttachment[]> => {
  if (!positId) return [];
  
  try {
    
    const { data, error } = await supabase
      .from('posit_attachments')
      .select('*')
      .eq('posit_id', positId);
    
    if (error) {
      throw error;
    }
    
    if (!data || data.length === 0) {
      return [];
    }
    
    
    // Generate signed URLs for each stored path for secure access
    const signed = await Promise.all(
      data.map(async (record) => {
        try {
          // record.file_url currently stores the storage path
          const { data: signedData, error: signError } = await supabase
            .storage
            .from('posit-attachments')
            .createSignedUrl(record.file_url, 3600);
          if (signError) {
          }
          return {
            id: record.file_id,
            name: record.file_name,
            mimeType: record.file_type,
            source: record.source as 'upload' | 'google_drive',
            url: signedData?.signedUrl, // may be undefined if signing failed
            isInitialFile: true
          } as FileAttachment;
        } catch (e) {
          return {
            id: record.file_id,
            name: record.file_name,
            mimeType: record.file_type,
            source: record.source as 'upload' | 'google_drive',
            url: undefined,
            isInitialFile: true
          } as FileAttachment;
        }
      })
    );
    
    return signed;
  } catch (error) {
    return [];
  }
};
